import { GithubLogo } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { githubReposQuery, type ImportRunOptions, type LinkedRepo, spacesQuery } from "@/components/admin/api";
import { ImportForm, RunHistory, RunPanel } from "@/components/admin/GithubImport";
import { drawerSearch, QueryBoundary } from "@/components/admin/shared";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import {
  Badge,
  type DataColumn,
  DataTable,
  EmptyState,
  RelativeTime,
  SectionHeader,
  TableSkeleton,
} from "@/components/ui";
import { useCatalogState } from "@/data/CatalogProvider";

export const Route = createFileRoute("/_app/admin/github")({
  validateSearch: drawerSearch("run"),
  component: GithubPage,
});

function GithubPage() {
  useBreadcrumbs([{ label: m.adm_title(), to: "/admin" }, { label: m.adm_nav_github() }]);
  const { run } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const repos = useQuery(githubReposQuery);
  const spaces = useQuery(spacesQuery);
  const { catalog } = useCatalogState();
  const [prefill, setPrefill] = useState<ImportRunOptions | null>(null);
  const [formVersion, setFormVersion] = useState(0);
  const formRef = useRef<HTMLElement>(null);

  const open = (id: string | null) => void navigate({ search: { run: id ?? undefined } });
  const productName = (id: string) => {
    for (const s of spaces.data ?? []) {
      const p = s.products.find((x) => x.id === id);
      if (p) return `${p.key}, ${p.name}`;
    }
    // The catalog is warm on every page; the spaces query may still be loading.
    const p = catalog.productById.get(id);
    return p ? `${p.key}, ${p.name}` : "";
  };

  const columns: DataColumn<LinkedRepo>[] = [
    {
      id: "repo",
      header: m.adm_gh_repo(),
      isRowHeader: true,
      cell: (r) => <span className="font-medium text-fg">{r.fullName}</span>,
    },
    {
      id: "product",
      header: m.adm_gh_linked_product(),
      cell: (r) => <span className="text-fg-muted">{productName(r.productId)}</span>,
    },
    {
      id: "branch",
      header: m.adm_gh_linked_branch(),
      className: "hidden sm:table-cell",
      cell: (r) => <span className="font-mono text-xs">{r.defaultBranch}</span>,
    },
    {
      id: "webhook",
      header: m.adm_gh_linked_webhook(),
      className: "hidden md:table-cell",
      cell: (r) =>
        r.hasWebhookSecret ? (
          <Badge tone="success">{m.ui_on()}</Badge>
        ) : (
          <Badge>{m.adm_gh_linked_webhook_default()}</Badge>
        ),
    },
    {
      id: "last",
      header: m.adm_gh_linked_last(),
      className: "hidden lg:table-cell",
      cell: (r) =>
        r.lastImportedAt ? (
          <RelativeTime value={r.lastImportedAt} className="text-fg-muted" />
        ) : (
          <span className="text-fg-subtle">{m.ui_never()}</span>
        ),
    },
  ];

  return (
    <Page title={m.adm_nav_github()} icon={GithubLogo} meta={m.adm_gh_meta()} width="full" className="max-w-5xl">
      <div className="flex flex-col gap-10">
        <section aria-labelledby="gh-linked" className="flex flex-col gap-3">
          <SectionHeader id="gh-linked" title={m.adm_gh_linked_title()} description={m.adm_gh_linked_body()} />
          <QueryBoundary query={repos} skeleton={<TableSkeleton rows={2} columns={4} />} needs="instance.admin">
            {(list) => (
              <DataTable
                label={m.adm_gh_linked_title()}
                columns={columns}
                rows={list}
                renderEmptyState={() => (
                  <EmptyState title={m.adm_gh_linked_empty_title()} description={m.adm_gh_linked_empty_body()} />
                )}
              />
            )}
          </QueryBoundary>
        </section>

        <section ref={formRef} aria-labelledby="gh-import" className="flex flex-col gap-4">
          <SectionHeader id="gh-import" title={m.adm_gh_import_title()} description={m.adm_gh_import_body()} />
          <div className="rounded-panel border border-line bg-surface p-5 shadow-[0_1px_2px_var(--shadow-soft)]">
            <ImportForm key={formVersion} initial={prefill} onStarted={(r) => open(r.id)} />
          </div>
          {run ? (
            <RunPanel
              key={run}
              id={run}
              onStarted={open}
              onEdit={(o) => {
                setPrefill(o);
                setFormVersion((v) => v + 1);
                formRef.current?.scrollIntoView({ block: "start" });
              }}
            />
          ) : null}
        </section>

        <section aria-labelledby="gh-history" className="flex flex-col gap-3">
          <h2 id="gh-history" className="text-base font-semibold tracking-snug text-fg">
            {m.adm_gh_hist_title()}
          </h2>
          <RunHistory activeId={run ?? null} onOpen={open} />
        </section>
      </div>
    </Page>
  );
}
